import { Card } from "@/components/ui"
import { cn } from "@/lib/cn"
import type { CSSProperties, ReactNode } from "react"

const SIZES = {
  md: { stubPx: 56, notchPx: 8, stub: "w-14", tear: "left-14 inset-y-3" },
  sm: { stubPx: 44, notchPx: 6, stub: "w-11", tear: "left-11 inset-y-2" },
} as const

function ticketMask(stubPx: number, notchPx: number): CSSProperties {
  const bite = (edge: "0" | "100%") => `radial-gradient(circle at ${stubPx}px ${edge}, transparent ${notchPx}px, black ${notchPx + 0.5}px)`
  const notches = `${bite("0")} top / 100% 51% no-repeat, ${bite("100%")} bottom / 100% 51% no-repeat`
  return { WebkitMask: notches, mask: notches }
}

const MASKS = { md: ticketMask(SIZES.md.stubPx, SIZES.md.notchPx), sm: ticketMask(SIZES.sm.stubPx, SIZES.sm.notchPx) } as const

type CouponTicketProps = {
  /** What sits on the stub, left of the perforation (the 3D coupon). */
  stub: ReactNode
  /** `md` for the coupon itself, `sm` for a coupon line in a list or a summary. */
  size?: keyof typeof SIZES
  as?: "div" | "li"
  busy?: boolean
  /** Fades the ticket for a coupon that is not on the order. */
  muted?: boolean
  children: ReactNode
}

/** The ivory coupon ticket of the game palette: a stub and a body split by a dashed tear line, bitten by two notches, softly lifted. */
export function CouponTicket({ stub, size = "md", as = "div", busy, muted = false, children }: CouponTicketProps) {
  const Outer = as
  const look = SIZES[size]
  return (
    <Outer className={size === "md" ? "drop-shadow-md" : "drop-shadow-sm"}>
      <Card variant="game" padding="none" aria-busy={busy} dimmed={muted ? "opacity" : false} style={MASKS[size]} className="relative flex">
        <span aria-hidden className={cn("pointer-events-none absolute w-0 border-l-2 border-dashed border-game-line", look.tear)} />
        <div className={cn("flex shrink-0 items-center justify-center", look.stub)}>{stub}</div>
        <div className="min-w-0 flex-1">{children}</div>
      </Card>
    </Outer>
  )
}
